import { useState, useEffect } from 'react';
import AnimatedHeading from '@/components/base/AnimatedHeading';
import FadeIn from '@/components/base/FadeIn';
import GradientBlinds from '@/components/base/GradientBlinds';
import Logo from '@/components/base/Logo';
import SmartLink from '@/components/base/SmartLink';
import StatsSection from '@/pages/home/components/StatsSection';
import VisionSection from '@/pages/home/components/VisionSection';
import TestimonialsSection from '@/pages/home/components/TestimonialsSection';
import CtaSection from '@/pages/home/components/CtaSection';
import Footer from '@/pages/home/components/Footer';
import { FlutedGlass } from '@paper-design/shaders-react';

function ScrollNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Our Mission', href: '/mission' },
    { label: 'What We Do', href: '/projects' },
    { label: 'About Us', href: '/about' },
  ];

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-md border-b border-white/5' : ''
        }`}
    >
      <div className="px-6 md:px-12 lg:px-16 pt-6 pb-4">
        <nav
          className={`rounded-xl px-4 py-2 flex items-center justify-between transition-all duration-300 ${scrolled ? 'bg-transparent' : 'liquid-glass'
            }`}
        >
          <SmartLink href="/">
            <Logo className="h-5 sm:h-6 md:h-7 w-auto text-white shrink-0" />
          </SmartLink>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <SmartLink
                key={link.label}
                href={link.href}
                className="text-sm text-white/80 hover:text-gray-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </SmartLink>
            ))}
          </div>
          <a href="mailto:hello@sapphiremedia.group">
            <button className="bg-white text-black px-3 py-1.5 sm:px-5 sm:py-2 md:px-6 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-1 sm:gap-1.5">
              Reach out
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="size-3.5 sm:size-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </button>
          </a>
        </nav>
      </div>
    </div>
  );
}

export default function Home() {
  const [exploreHovered, setExploreHovered] = useState(false);

  return (
    <div className="bg-black">
      <ScrollNavbar />

      {/* Hero - full viewport */}
      <div className="relative w-full h-screen overflow-hidden flex flex-col">
        {/* GradientBlinds Background */}
        <div className="absolute inset-0 z-0" style={{ pointerEvents: 'all' }}>
          {/* <GradientBlinds
            gradientColors={['#0a0a0a', '#1a1a2e', '#16213e', '#0f3460', '#533483', '#e94560']}
            angle={0}
            noise={0.25}
            blindCount={24}
            blindMinWidth={50}
            spotlightRadius={0.55}
            spotlightSoftness={1.2}
            spotlightOpacity={0.9}
            mouseDampening={0.12}
            distortAmount={0}
            shineDirection="left"
            mixBlendMode="normal"
          /> */}


          <FlutedGlass
            width="100%"
            height="100%"
            image="https://shaders.paper.design/images/image-filters/004.webp"
            colorBack="#3e323200"
            colorShadow="#000000"
            colorHighlight="#ffffff"
            size={0.5}
            shadows={0.25}
            highlights={0.1}
            shape="lines"
            angle={0}
            distortionShape="prism"
            distortion={0.5}
            shift={0}
            stretch={0}
            blur={0}
            edges={0.25}
            margin={0}
            grainMixer={0}
            grainOverlay={0}
            scale={1.05}
            fit="cover"
          />

        </div>

        {/* Readability scrim — darkens top (navbar) and bottom (hero text) without flattening the shader */}
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 30%, rgba(0,0,0,0) 60%), ' +
              'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 25%)',
          }}
        />

        {/* Spacer for fixed navbar */}
        <div className="h-[88px] shrink-0" />

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-12 lg:pb-16" style={{ pointerEvents: 'none' }}>
          <div className="lg:grid lg:grid-cols-2 lg:items-end gap-8">
            {/* Left Column */}
            <div style={{ pointerEvents: 'auto' }}>
              <h1
                className="font-['Instrument_Serif'] text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-normal text-white mb-4"
                style={{ letterSpacing: '-0.03em' }}
              >
                <AnimatedHeading
                  text={"Built by youth,\nbroadcasting what's next."}
                  initialDelay={200}
                  charDelay={30}
                  duration={500}
                />
              </h1>

              <FadeIn delay={800} duration={1000}>
                <p className="text-base md:text-lg text-gray-300 mb-5">
                  A youth-led non-profit creating software, stories, and radio that put the next generation on the air.
                </p>
              </FadeIn>

              <FadeIn delay={1200} duration={1000}>
                <div className="flex flex-wrap gap-4">
                  <a href="https://blissradio.org">
                    <button className="bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                      Tune In
                    </button>
                  </a>
                  <a href="https://discord.gg/K2zNSVTT9e">
                    <button
                      className="liquid-glass border border-white/20 text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                      style={{
                        backgroundColor: exploreHovered ? 'white' : undefined,
                        color: exploreHovered ? 'black' : undefined,
                      }}
                      onMouseEnter={() => setExploreHovered(true)}
                      onMouseLeave={() => setExploreHovered(false)}
                    >
                      Join our Discord
                    </button>
                  </a>
                </div>
              </FadeIn>
            </div>

            {/* Right Column - Tag */}
            {/* <div className="flex items-end justify-start lg:justify-end mt-8 lg:mt-0">
              <FadeIn delay={1400} duration={1000}>
                <div className="liquid-glass border border-white/20 px-6 py-3 rounded-xl" style={{ pointerEvents: 'auto' }}>
                  <span className="text-lg md:text-xl lg:text-2xl font-light text-white">
                    Giving youth a voice
                  </span>
                </div>
              </FadeIn>
            </div> */}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-40" style={{ pointerEvents: 'auto' }}>
          <span className="text-xs text-white tracking-[0.2em] uppercase">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-white to-transparent" />
        </div>
      </div>

      {/* Streamlined sections */}
      <StatsSection />
      <VisionSection />
      <TestimonialsSection />
      <CtaSection />
      <Footer />
    </div>
  );
}
